import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { compose, setDisplayName, withState } from 'recompose'
import { noop } from 'lodash'
import { Popper } from 'react-popper'

import { PLACEMENT_POSITIONS } from 'utils/constants/components/popover'
import { DOMElementType } from 'utils/helpers/propTypes'
import { Overlay } from 'components/overlay/Overlay'
import { Portal } from 'components/portal/Portal'

import styles from './Popover.module.scss'

const ReactPopperBoundariesElementType = PropTypes.oneOfType([
  PropTypes.oneOf(['scrollParent', 'window', 'viewport']),
  DOMElementType,
])

const ReactPopperPreventOverflowShape = PropTypes.shape({
  order: PropTypes.number,
  enabled: PropTypes.bool,
  fn: PropTypes.func,
  priority: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  padding: PropTypes.number,
  boundariesElement: ReactPopperBoundariesElementType,
})

const ReactPopperFlipShape = PropTypes.shape({
  order: PropTypes.number,
  enabled: PropTypes.bool,
  fn: PropTypes.func,
  behavior: PropTypes.oneOfType([
    PropTypes.oneOf(['flip', 'clockwise', 'counterclockwise']),
    PropTypes.arrayOf(PropTypes.oneOf(PLACEMENT_POSITIONS)),
  ]),
  padding: PropTypes.number,
  boundariesElement: ReactPopperBoundariesElementType,
})

const PopoverCmp = ({
  referenceClassName,
  children,
  childrenRef,
  targetRef,
  setChildrenRef,
  setTargetRef,
  // overlay props
  isOpen,
  onHide = noop,
  // popper props
  placement = 'bottom',
  offset,
  flip = {
    enabled: true,
    behavior: ['bottom', 'top'],
    boundariesElement: 'window',
  },
  preventOverflow = {
    enabled: false,
  },
  positionFixed = false,
  excludedElement,
}) => {
  // NOTE: move specific modifier options to props if needed
  const popperModifiers = {
    flip,
    preventOverflow,
    keepTogether: {
      enabled: false,
    },
    arrow: {
      enabled: false,
    },
    hide: {
      enabled: false,
    },
  }

  if (offset) {
    popperModifiers.offset = {
      offset,
    }
  }

  return (
    <div ref={setChildrenRef} className={classNames(styles.reference, referenceClassName)}>
      <Overlay container={targetRef} isOpen={isOpen} onHide={onHide} excludedElement={excludedElement}>
        <Portal containerEl={document.body}>
          <Popper
            innerRef={setTargetRef}
            positionFixed={positionFixed}
            placement={placement}
            referenceElement={childrenRef}
            modifiers={popperModifiers}>
            {({ ref, style, scheduleUpdate }) => (
              <div className={styles.container} ref={ref} style={style}>
                {typeof children === 'function' ? children({ scheduleUpdate }) : children}
              </div>
            )}
          </Popper>
        </Portal>
      </Overlay>
    </div>
  )
}

PopoverCmp.propTypes = {
  referenceClassName: PropTypes.any,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  childrenRef: PropTypes.any,
  targetRef: PropTypes.any,
  setChildrenRef: PropTypes.func.isRequired,
  setTargetRef: PropTypes.func.isRequired,
  // overlay props
  isOpen: PropTypes.bool.isRequired,
  onHide: PropTypes.func,
  // popper props
  placement: PropTypes.oneOf(PLACEMENT_POSITIONS),
  offset: PropTypes.string,
  positionFixed: PropTypes.bool,
  flip: ReactPopperFlipShape,
  preventOverflow: ReactPopperPreventOverflowShape,
  excludedElement: PropTypes.any,
}

export const Popover = compose(
  setDisplayName('Popover'),
  withState('childrenRef', 'setChildrenRef', null),
  withState('targetRef', 'setTargetRef', null),
)(PopoverCmp)

Popover.propTypes = {
  referenceClassName: PropTypes.any,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  // overlay props
  isOpen: PropTypes.bool.isRequired,
  onHide: PropTypes.func,
  // popper props
  placement: PropTypes.oneOf(PLACEMENT_POSITIONS),
  offset: PropTypes.string,
  flip: ReactPopperFlipShape,
  preventOverflow: ReactPopperPreventOverflowShape,
  positionFixed: PropTypes.bool,
  excludedElement: PropTypes.any,
}
